// useRailIconMode.js
// Observes the parent grid cell of the rail Stack and returns whether the rail
// should render in icon-only mode (cell width < ICON_WIDTH_THRESHOLD).
//
// Extracted from MarginRail so the component body stays focused on rendering.
// The hysteresis prevents rapid full↔icon toggling at the boundary.

import {useEffect, useRef, useState} from 'react';

const ICON_WIDTH_THRESHOLD = 280; // px — cell width below which icon mode activates
const ICON_HYSTERESIS = 16;       // px — prevents rapid toggling at the boundary

/**
 * @param {React.RefObject} railRef - ref attached to the rail's root DOM node.
 *   The hook observes railRef.current.parentElement (the grid cell), not the
 *   rail itself, because the rail sets its own maxWidth which would always
 *   read back as 320px.
 * @returns {boolean} isIconMode
 */
export function useRailIconMode(railRef) {
    const isIconRef = useRef(false);
    const [isIconMode, setIsIconMode] = useState(false);

    useEffect(() => {
        const cell = railRef.current?.parentElement;
        if (!cell) return;

        function evaluate(width) {
            // Hysteresis: only flip when we clearly cross the boundary in each direction.
            const next = isIconRef.current
                ? width < ICON_WIDTH_THRESHOLD + ICON_HYSTERESIS
                : width < ICON_WIDTH_THRESHOLD;

            if (next !== isIconRef.current) {
                isIconRef.current = next;
                setIsIconMode(next);
            }
        }

        const observer = new ResizeObserver(([entry]) => {
            const w = entry.contentBoxSize
                ? entry.contentBoxSize[0].inlineSize
                : entry.contentRect.width;
            evaluate(w);
        });

        observer.observe(cell);
        evaluate(cell.getBoundingClientRect().width);

        return () => observer.disconnect();
    }, [railRef]);

    return isIconMode;
}
